import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import _ from 'lodash';
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Flex,
  Space,
  Steps,
  Tag,
  Tooltip,
  Typography,
  theme,
} from '@duxton-web/ui';
import {
  CloseCircleFilled,
  DocumentOutlined,
  LaunchUniversal,
  StopUniversal,
  TickMarkFilled,
  WarningFilled,
} from '@duxton-web/icons';
import { getCampaignUrl } from './debuggingToolsConstants';
import {
  StatusTone,
  effectiveEndState,
  effectiveStoppedAt,
  endStateLabel,
  endStateTone,
  getLatestEventTime,
  humanizeReason,
  humanizeStatus,
  isDeliveryUnrecorded,
  isRetryableXco,
  stageLabel,
  stageTitle,
  stageTone,
  stoppedAtLabel,
} from './commsTraceStatus';
import {
  COMMS_TRACE_STAGE_KEYS,
  CommsTraceHedwigComponent,
  CommsTraceJourney,
  CommsTraceStageKey,
  CommsTraceXcoAttempt,
} from './types';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export const formatDateTimeWithZone = (
  value: string | null | undefined,
  timezone?: string,
  timezoneCode?: string,
): string => {
  if (!value) return '-';
  try {
    const date = timezone ? dayjs(value).tz(timezone) : dayjs(value);
    if (!date.isValid()) return '-';
    const formatted = date.format('YYYY-MM-DD HH:mm:ss');
    return timezoneCode ? `${formatted} ${timezoneCode}` : formatted;
  } catch (e) {
    return '-';
  }
};

type DescriptionItem = {
  key: string;
  label: React.ReactNode;
  children: React.ReactNode;
};

type FunnelStage = {
  key: CommsTraceStageKey;
  title: string;
  statusLabel: string;
  tone: StatusTone;
  content: React.ReactNode;
};

const getContentUrl = (templateID: string): string =>
  `${window.Grab.config.HEDWIG_DASH_URI}/templates/create/push/?id=${templateID}`;

const compactText = (value: string, visibleChars = 28): string => {
  if (value.length <= visibleChars) return value;
  const prefixLength = Math.max(8, Math.floor(visibleChars * 0.6));
  const suffixLength = Math.max(4, visibleChars - prefixLength - 1);
  return `${value.slice(0, prefixLength)}…${value.slice(-suffixLength)}`;
};

const getToneColor = (tone: StatusTone): string => {
  switch (tone) {
    case 'success':
      return 'green';
    case 'error':
      return 'red';
    case 'warning':
      return 'orange';
    default:
      return 'default';
  }
};

const getStepStatus = (tone: StatusTone): 'finish' | 'error' | 'process' | 'wait' => {
  switch (tone) {
    case 'success':
      return 'finish';
    case 'error':
      return 'error';
    case 'warning':
      return 'process';
    default:
      return 'wait';
  }
};

const getStatusIcon = (
  tone: StatusTone,
  defaultIcon?: React.ReactNode,
): React.ReactNode => {
  switch (tone) {
    case 'success':
      return <TickMarkFilled />;
    case 'error':
      return <CloseCircleFilled />;
    case 'warning':
      return <WarningFilled />;
    default:
      return defaultIcon;
  }
};

const missing = <Typography.Text type="secondary">-</Typography.Text>;

const textOrMissing = (value: string | null | undefined): React.ReactNode =>
  (value || missing);

const scoreValue = (score: number | null | undefined): string =>
  (score == null ? '-' : score.toFixed(6));

const flagValue = (flag: number | null | undefined): string => {
  if (flag == null) return '-';
  return flag ? 'Yes' : 'No';
};

const TruncatedId: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  if (!value) return <React.Fragment>-</React.Fragment>;
  const isLong = value.length > 36;
  const display = compactText(value, 36);
  return (
    <Tooltip title={isLong ? value : undefined}>
      <Typography.Metadata copyable={{ text: value }}>
        {display}
      </Typography.Metadata>
    </Tooltip>
  );
};

const StatusTag: React.FC<{ tone: StatusTone; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <Tag color={getToneColor(tone)} icon={getStatusIcon(tone)}>
    {children}
  </Tag>
);

const StageTitle: React.FC<{
  title: string;
  statusLabel: string;
  tone: StatusTone;
}> = ({ title, statusLabel, tone }) => (
  <Flex align="center" gap={8} wrap="wrap">
    <Typography.Title level={5} style={{ margin: 0 }}>
      {title}
    </Typography.Title>
    <StatusTag tone={tone}>{statusLabel}</StatusTag>
  </Flex>
);

const StepMarker: React.FC<{ tone: StatusTone; token: any }> = ({ tone, token }) => {
  const toneStyles = {
    success: {
      background: token.colorSuccessBg || '#e8fff1',
      border: token.colorSuccessBorder || '#7be7a2',
      color: token.colorSuccess || '#008f46',
    },
    error: {
      background: token.colorErrorBg || '#fff1f0',
      border: token.colorErrorBorder || '#ff8a86',
      color: token.colorError || '#d42e1c',
    },
    warning: {
      background: token.colorWarningBg || '#fff7e6',
      border: token.colorWarningBorder || '#ffd591',
      color: token.colorWarning || '#f09800',
    },
    default: {
      background: token.colorFillTertiary || '#f5f5f5',
      border: token.colorBorder || '#d9d9d9',
      color: token.colorTextTertiary || '#8c8c8c',
    },
  }[tone];
  const icon = getStatusIcon(tone, <StopUniversal />);

  return (
    <span
      style={{
        alignItems: 'center',
        background: toneStyles.background,
        border: `1px solid ${toneStyles.border}`,
        borderRadius: '50%',
        color: toneStyles.color,
        display: 'inline-flex',
        height: 28,
        justifyContent: 'center',
        width: 28,
      }}
    >
      {icon}
    </span>
  );
};

const DetailDescriptions: React.FC<{
  items: DescriptionItem[];
}> = ({ items }) => (
  <Descriptions
    colon={false}
    column={1}
    items={items}
    size="small"
    styles={{
      content: { textAlign: 'right' },
      label: { width: 170 },
    }}
  />
);

const DetailCard: React.FC<{
  children: React.ReactNode;
  token: any;
}> = ({ children, token }) => (
  <Card
    size="small"
    style={{
      borderColor: token.colorBorderSecondary,
      marginTop: 8,
    }}
    styles={{ body: { padding: '8px 12px' } }}
  >
    {children}
  </Card>
);

const MetadataItem: React.FC<{
  label: string;
  children: React.ReactNode;
  token: any;
}> = ({ label, children, token }) => (
  <div
    style={{
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: token.borderRadiusLG,
      minWidth: 0,
      padding: '10px 12px',
    }}
  >
    <Typography.Metadata type="secondary">{label}</Typography.Metadata>
    <div style={{ marginTop: 4 }}>{children}</div>
  </div>
);

const renderStatusTag = (
  journey: CommsTraceJourney,
  stageKey: CommsTraceStageKey,
): React.ReactNode => {
  const stage = journey.stages?.[stageKey];
  if (!stage) return missing;
  return (
    <StatusTag tone={stageTone(journey, stageKey)}>
      {stageKey === 'hedwig_delivery' && isDeliveryUnrecorded(journey)
        ? stageLabel(journey, stageKey)
        : humanizeStatus(stage.status)}
    </StatusTag>
  );
};

const renderMessage = (component: CommsTraceHedwigComponent): React.ReactNode => {
  if (!component.title && !component.content) return missing;
  return (
    <Flex vertical gap={2} style={{ textAlign: 'left' }}>
      {component.title && <Typography.Text strong>{component.title}</Typography.Text>}
      {component.content && (
        <Tooltip
          title={<span style={{ whiteSpace: 'pre-line' }}>{component.content}</span>}
        >
          <Typography.Paragraph
            copyable={{ text: component.content }}
            ellipsis={{ rows: 3 }}
            style={{ marginBottom: 0, whiteSpace: 'pre-line' }}
          >
            {component.content}
          </Typography.Paragraph>
        </Tooltip>
      )}
    </Flex>
  );
};

const componentTone = (status: string): StatusTone => {
  if (status === 'SENT' || status === 'RECEIVED') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'UNKNOWN') return 'warning';
  return 'default';
};

const renderErrorDetail = (component: CommsTraceHedwigComponent): React.ReactNode => {
  if (!component.status_title && !component.status_details) return missing;
  return (
    <Flex vertical style={{ textAlign: 'left' }}>
      {component.status_title && <Typography.Text>{component.status_title}</Typography.Text>}
      {component.status_details && (
        <Typography.Text type="secondary">{component.status_details}</Typography.Text>
      )}
    </Flex>
  );
};

const buildComponentItems = (
  component: CommsTraceHedwigComponent,
  timezone: string,
  timezoneCode?: string,
): DescriptionItem[] => [
  {
    key: 'status',
    label: 'Status',
    children: component.status
      ? (
        <StatusTag tone={componentTone(component.status)}>
          {humanizeStatus(component.status)}
        </StatusTag>
      )
      : missing,
  },
  { key: 'messageId', label: 'Message ID', children: <TruncatedId value={component.message_id} /> },
  { key: 'eventTime', label: 'Event time', children: formatDateTimeWithZone(component.event_time, timezone, timezoneCode) },
  { key: 'channel', label: 'Channel', children: textOrMissing(component.channel?.toLowerCase()) },
  { key: 'category', label: 'Template category', children: textOrMissing(component.category) },
  { key: 'callingService', label: 'Calling service', children: textOrMissing(component.callingservice) },
  { key: 'sendMode', label: 'Send mode', children: textOrMissing(component.send_mode) },
  { key: 'errorDetail', label: 'Error detail', children: renderErrorDetail(component) },
  { key: 'message', label: 'Message', children: renderMessage(component) },
];

const renderHedwigDelivery = (
  journey: CommsTraceJourney,
  token: any,
  timezone: string,
  timezoneCode?: string,
): React.ReactNode => {
  const stage = journey.stages?.hedwig_delivery;
  if (!stage) return null;
  const components = _.sortBy(stage.components || [], 'event_time');
  if (!components.length) {
    return (
      <DetailCard token={token}>
        <DetailDescriptions
          items={[
            { key: 'status', label: 'Status', children: renderStatusTag(journey, 'hedwig_delivery') },
          ]}
        />
      </DetailCard>
    );
  }
  return (
    <React.Fragment>
      {components.map((component, i) => (
        <DetailCard key={`${component.message_id}-${component.event_time}-${i}`} token={token}>
          <DetailDescriptions items={buildComponentItems(component, timezone, timezoneCode)} />
        </DetailCard>
      ))}
    </React.Fragment>
  );
};

const renderAttempts = (
  attempts: CommsTraceXcoAttempt[],
  timezone: string,
  timezoneCode?: string,
): React.ReactNode => {
  if (!attempts.length) return missing;
  return (
    <Flex vertical gap={4} align="flex-end">
      {_.sortBy(attempts, 'dispatch_date').map((attempt, i) => {
        const retryable = isRetryableXco(attempt.status);
        let tone: StatusTone = 'default';
        if (retryable) tone = 'warning';
        else if (attempt.status === 'SELECTED') tone = 'success';
        else if (attempt.status) tone = 'error';
        return (
          <Flex key={`${attempt.dispatch_date}-${i}`} align="center" gap={8} wrap="wrap" justify="flex-end">
            <Typography.Text>{attempt.dispatch_date}</Typography.Text>
            <StatusTag tone={tone}>{humanizeStatus(attempt.status)}</StatusTag>
            <Tag color="purple">
              {formatDateTimeWithZone(attempt.event_time, timezone, timezoneCode)}
            </Tag>
            {retryable && <Typography.Text type="secondary">retried next day</Typography.Text>}
          </Flex>
        );
      })}
    </Flex>
  );
};

const buildStageContent = (
  journey: CommsTraceJourney,
  key: CommsTraceStageKey,
  timezone: string,
  timezoneCode?: string,
): DescriptionItem[] | null => {
  const eventTimeItem = (value: string | null | undefined): DescriptionItem => ({
    key: 'eventTime',
    label: 'Event time',
    children: value ? formatDateTimeWithZone(value, timezone, timezoneCode) : missing,
  });
  const stages = journey.stages;
  switch (key) {
    case 'relevance_filter': {
      const stage = stages?.relevance_filter;
      if (!stage) return null;
      const campaign = journey.campaign;
      return [
        { key: 'status', label: 'Status', children: renderStatusTag(journey, key) },
        eventTimeItem(stage.event_time),
        { key: 'score', label: 'Score', children: scoreValue(stage.score) },
        {
          key: 'dispatchTime',
          label: 'Dispatch time',
          children: formatDateTimeWithZone(stage.dispatch_time, timezone, timezoneCode),
        },
        {
          key: 'submissionDate',
          label: 'Submission date',
          children: textOrMissing(stage.submission_date),
        },
        {
          key: 'campaignType',
          label: 'Campaign type',
          children: campaign ? (campaign.is_trident ? 'Trident' : 'SmartComms') : '-',
        },
        { key: 'channel', label: 'Channel', children: textOrMissing(campaign?.channel) },
        { key: 'xcoEnabled', label: 'XCO enabled', children: flagValue(campaign?.xco_enabled) },
        {
          key: 'attributePairing',
          label: 'Content injection campaign',
          children: flagValue(campaign?.is_attributepairing),
        },
      ];
    }
    case 'prefilter': {
      const stage = stages?.prefilter;
      if (!stage) return null;
      return [
        { key: 'status', label: 'Status', children: renderStatusTag(journey, key) },
        eventTimeItem(stage.event_time),
        { key: 'reason', label: 'Reason', children: textOrMissing(stage.reason && humanizeReason(stage.reason)) },
      ];
    }
    case 'similarity_check': {
      const stage = stages?.similarity_check;
      if (!stage) return null;
      return [
        { key: 'status', label: 'Status', children: renderStatusTag(journey, key) },
        eventTimeItem(stage.event_time),
        { key: 'original', label: 'Original score', children: scoreValue(stage.original_score) },
        { key: 'final', label: 'Final score', children: scoreValue(stage.final_score) },
      ];
    }
    case 'xco': {
      const stage = stages?.xco;
      if (!stage) return null;
      return [
        { key: 'status', label: 'Status', children: renderStatusTag(journey, key) },
        {
          key: 'attempts',
          label: 'Attempts',
          children: renderAttempts(stage.attempts || [], timezone, timezoneCode),
        },
      ];
    }
    default:
      return null;
  }
};

const buildFunnelStages = (
  journey: CommsTraceJourney,
  token: any,
  timezone: string,
  timezoneCode?: string,
): FunnelStage[] =>
  COMMS_TRACE_STAGE_KEYS.map(key => {
    const items = buildStageContent(journey, key, timezone, timezoneCode);
    let content: React.ReactNode = null;
    if (key === 'hedwig_delivery') {
      content = renderHedwigDelivery(journey, token, timezone, timezoneCode);
    } else if (items) {
      content = (
        <DetailCard token={token}>
          <DetailDescriptions items={items} />
        </DetailCard>
      );
    }
    return {
      key,
      title: stageTitle(key),
      statusLabel: stageLabel(journey, key),
      tone: stageTone(journey, key),
      content,
    };
  });

const hasStageData = (journey: CommsTraceJourney, key: CommsTraceStageKey): boolean =>
  Boolean(journey.stages?.[key]) && !(key === 'hedwig_delivery' && isDeliveryUnrecorded(journey));

const getCurrentStageIndex = (journey: CommsTraceJourney): number => {
  const stoppedAt = effectiveStoppedAt(journey);
  if (stoppedAt) {
    const stoppedIndex = COMMS_TRACE_STAGE_KEYS.indexOf(stoppedAt);
    if (stoppedIndex >= 0) return stoppedIndex;
  }
  for (let i = COMMS_TRACE_STAGE_KEYS.length - 1; i >= 0; i -= 1) {
    if (hasStageData(journey, COMMS_TRACE_STAGE_KEYS[i])) return i;
  }
  return 0;
};

interface DebuggingToolsDrawerProps {
  journey: CommsTraceJourney | null;
  timezone?: string;
  timezoneCode?: string;
  open: boolean;
  onClose: () => void;
}

const DebuggingToolsDrawer: React.FC<DebuggingToolsDrawerProps> = ({
  journey,
  timezone = 'UTC',
  timezoneCode,
  open,
  onClose,
}) => {
  const { token } = theme.useToken();
  const funnelStages = journey ? buildFunnelStages(journey, token, timezone, timezoneCode) : [];
  const campaignUrl = journey
    ? getCampaignUrl(
      journey.tracking_id,
      journey.campaign?.id,
      journey.campaign?.country_id,
    )
    : '';
  const endState = journey ? effectiveEndState(journey) : null;
  const endTone = endStateTone(endState);

  return (
    <Drawer
      title={
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Title level={4} style={{ margin: 0 }}>
            Where's My Comms
          </Typography.Title>
          {journey && (
            <StatusTag tone={endTone}>{endStateLabel(endState)}</StatusTag>
          )}
        </Flex>
      }
      width="min(1120px, calc(100vw - 32px))"
      open={open}
      onClose={onClose}
      zIndex={1100}
      styles={{
        body: {
          background: token.colorBgLayout || '#f7f8fa',
          padding: '20px 24px',
        },
        content: {
          background: token.colorBgLayout || '#f7f8fa',
        },
        header: {
          background: token.colorBgLayout || '#f7f8fa',
        },
      }}
    >
      {journey && (
        <Space direction="vertical" size={20} style={{ display: 'flex' }}>
          <div
            style={{
              display: 'grid',
              gap: 12,
              gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            }}
          >
            <MetadataItem label="User ID" token={token}>
              <Flex align="center" gap={8}>
                <Typography.Text strong>{journey.user_id || '-'}</Typography.Text>
                {journey.user_type && (
                  <Tag>{journey.user_type}</Tag>
                )}
              </Flex>
            </MetadataItem>
            <MetadataItem label="Campaign" token={token}>
              {journey.campaign ? (
                <Tooltip title={journey.campaign.name}>
                  {campaignUrl ? (
                    <Typography.Link href={campaignUrl} target="_blank" rel="noreferrer">
                      {compactText(journey.campaign.name || '', 34)}
                    </Typography.Link>
                  ) : (
                    <Typography.Text>{compactText(journey.campaign.name || '', 34)}</Typography.Text>
                  )}
                </Tooltip>
              ) : (
                missing
              )}
            </MetadataItem>
            <MetadataItem label="Tracking ID" token={token}>
              <TruncatedId value={journey.tracking_id} />
            </MetadataItem>
            <MetadataItem label="Template ID" token={token}>
              {journey.template_id ? (
                <Tooltip title={journey.template_id}>
                  <Typography.Link
                    href={getContentUrl(journey.template_id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {compactText(journey.template_id, 30)}
                  </Typography.Link>
                </Tooltip>
              ) : (
                missing
              )}
            </MetadataItem>
          </div>

          <div
            style={{
              alignItems: 'start',
              display: 'grid',
              gap: 20,
              gridTemplateColumns: 'minmax(0, 1fr) 320px',
            }}
          >
            <Steps
              current={getCurrentStageIndex(journey)}
              direction="vertical"
              items={funnelStages.map(stage => ({
                key: stage.key,
                title: (
                  <StageTitle
                    title={stage.title}
                    statusLabel={stage.statusLabel}
                    tone={stage.tone}
                  />
                ),
                description: stage.content,
                icon: <StepMarker tone={stage.tone} token={token} />,
                status: getStepStatus(stage.tone),
              }))}
            />

            <Space direction="vertical" size={16} style={{ display: 'flex' }}>
              <Card
                title="Outcome"
                size="small"
                styles={{ body: { padding: 16 } }}
              >
                <Space direction="vertical" size={12} style={{ display: 'flex' }}>
                  <Flex align="center" gap={8} wrap="wrap">
                    <Typography.Title
                      level={3}
                      style={{ margin: 0 }}
                      type={endTone === 'error' ? 'danger' : undefined}
                    >
                      {endStateLabel(endState)}
                    </Typography.Title>
                    {journey.reason && !isDeliveryUnrecorded(journey) && (
                      <StatusTag tone={endTone}>{humanizeReason(journey.reason)}</StatusTag>
                    )}
                  </Flex>
                  <DetailDescriptions
                    items={[
                      {
                        key: 'stage',
                        label: 'Stopped at',
                        children: stoppedAtLabel(effectiveStoppedAt(journey)),
                      },
                      {
                        key: 'time',
                        label: 'Timestamp',
                        children: formatDateTimeWithZone(getLatestEventTime(journey), timezone, timezoneCode),
                      },
                    ]}
                  />
                </Space>
              </Card>

              <Card
                title="Trace summary"
                size="small"
                styles={{ body: { padding: 16 } }}
              >
                <DetailDescriptions
                  items={[
                    { key: 'total', label: 'Total stages', children: funnelStages.length },
                    {
                      key: 'passed',
                      label: 'Passed',
                      children: funnelStages.filter(stage => stage.tone === 'success').length,
                    },
                    {
                      key: 'skipped',
                      label: 'Skipped',
                      children: funnelStages.filter(
                        stage => stage.tone !== 'error' && !hasStageData(journey, stage.key),
                      ).length,
                    },
                    {
                      key: 'dropped',
                      label: 'Dropped',
                      children: funnelStages.filter(stage => stage.tone === 'error').length,
                    },
                  ]}
                />
              </Card>

              <Card
                title="Debug actions"
                size="small"
                styles={{ body: { padding: 16 } }}
              >
                <Space direction="vertical" size={8} style={{ display: 'flex' }}>
                  <Button
                    block
                    disabled={!campaignUrl}
                    href={campaignUrl || undefined}
                    icon={<LaunchUniversal />}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open campaign
                  </Button>
                  <Button
                    block
                    disabled={!journey.template_id}
                    href={journey.template_id ? getContentUrl(journey.template_id) : undefined}
                    icon={<DocumentOutlined />}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open template
                  </Button>
                </Space>
              </Card>
            </Space>
          </div>
        </Space>
      )}
    </Drawer>
  );
};

export default DebuggingToolsDrawer;
